import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

const calculateVerticalPercentage = (bounds, threshold = 0, root = window) => {
  const scrollRoot =
    root && root !== window && typeof root.getBoundingClientRect === "function"
      ? root
      : typeof document !== "undefined"
      ? document.getElementById("scrollytell-content")
      : null;
  const rootBounds =
    scrollRoot && typeof scrollRoot.getBoundingClientRect === "function"
      ? scrollRoot.getBoundingClientRect()
      : { top: 0, height: typeof window !== "undefined" ? window.innerHeight : 800 };

  const percentage =
    ((bounds.top - rootBounds.top) * -1) / (bounds.height - rootBounds.height);
  return Math.min(1, Math.max(0, percentage));
};

const calculateHorizontalPercentage = (
  bounds,
  threshold = 0,
  root = window
) => {
  if (!root) {
    return 0;
  }
  const vw =
    (root instanceof Element ? root.clientWidth : root.innerWidth) || 0;
  const offset = threshold * bounds.width;
  const percentage = (bounds.right - offset) / (vw + bounds.width - offset * 2);

  return 1 - Math.max(0, Math.min(1, percentage));
};

export function useScrollPercentage(options = {}) {
  const [ref, inView, entry] = useInView(options);
  const [percentage, setPercentage] = useState(0);
  const target = entry && entry.target;

  useEffect(() => {
    const handleScroll = () => {
      if (!target) return;
      const bounds = target.getBoundingClientRect();
      const percentage = options.horizontal
        ? calculateHorizontalPercentage(bounds, options.threshold, options.root)
        : calculateVerticalPercentage(bounds, options.threshold, options.root);

      setPercentage(percentage);
    };

    const scrollRoot =
      options.root ||
      (typeof document !== "undefined"
        ? document.getElementById("scrollytell-content")
        : null) ||
      window;

    if (scrollRoot && typeof scrollRoot.addEventListener === "function") {
      scrollRoot.addEventListener("scroll", handleScroll, { passive: true });
    }
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll, { passive: true });
      window.addEventListener("resize", handleScroll);
    }

    handleScroll();

    return () => {
      if (scrollRoot && typeof scrollRoot.removeEventListener === "function") {
        scrollRoot.removeEventListener("scroll", handleScroll);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleScroll);
      }
    };
  }, [options.root, options.horizontal, options.threshold, target]);

  return [ref, percentage, entry];
}
